import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { formatQ, monthlyPayment } from '../utils/format';
const ANNUAL_RATE = 0.18;
const PLAZOS = [12, 24, 36, 48, 60];
const MIN_ENGANCHE = 10000;
interface CreditSimulatorProps {
  onSolicitar: (resumen: string) => void;
}
export function CreditSimulator({
  onSolicitar
}: CreditSimulatorProps) {
  const [precio, setPrecio] = useState(45000);
  const [engancheInput, setEngancheInput] = useState(12000);
  const [plazo, setPlazo] = useState(36);

  // El enganche va de Q10,000 (mínimo) hasta el 40% del valor del lote
  const maxEnganche = Math.round(precio * 0.4);
  const enganche = Math.min(Math.max(engancheInput, MIN_ENGANCHE), maxEnganche);
  const enganchePct = Math.round(enganche / precio * 100);
  const financiar = precio - enganche;
  const cuota = monthlyPayment(financiar, ANNUAL_RATE, plazo);
  const total = enganche + cuota * plazo;
  return <section id="simulador" className="bg-sand py-20 lg:py-28" data-id="element-69" data-mp="3sjac9zslhsfi">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-70" data-mp="3gz138hva0qev">
        <SectionHeading title="Simulador de crédito directo" description="Sin banco, sin historial crediticio. Ajuste su lote, su enganche y su plazo para ver la cuota mensual estimada." data-id="element-71" data-mp="3cxfhe70rl1sj" />

        <div className="mt-14 grid overflow-hidden rounded-sm bg-white shadow-card lg:grid-cols-[1.15fr_1fr]" data-id="element-72" data-mp="3372z6lgf9kpk">
          {/* Controles */}
          <div className="space-y-9 p-7 sm:p-10" data-id="element-73" data-mp="2y6ygcos1kdw6">
            <div data-id="element-74" data-mp="2t9ou1hzy8ick">
              <div className="flex items-baseline justify-between" data-id="element-75" data-mp="2t96qe0ayj1um">
                <label htmlFor="precio" className="text-[12px] font-semibold uppercase tracking-[0.14em] text-forest-900/70" data-id="element-76" data-mp="2p7uvpgd87qfk">
                  Precio del lote
                </label>
                <span className="font-display text-2xl text-forest-900" data-id="element-77" data-mp="nh01uu2ky1bh">{formatQ(precio)}</span>
              </div>
              <input id="precio" type="range" className="arco-slider mt-4" min={35000} max={75000} step={1000} value={precio} onChange={e => setPrecio(Number(e.target.value))} data-id="element-78" data-mp="ihdn2g73sd0k" />
              <div className="mt-2 flex justify-between text-[11px] uppercase tracking-[0.1em] text-forest-900/45" data-id="element-79" data-mp="smwpj5avhaeh">
                <span data-id="element-80" data-mp="xlgbh5pgum6u">Q35,000</span>
                <span data-id="element-81" data-mp="1laj418ln1zg4">Q75,000</span>
              </div>
            </div>

            <div data-id="element-82" data-mp="1lbamriu502oz">
              <div className="flex items-baseline justify-between" data-id="element-83" data-mp="1l8oknyq0mfe5">
                <label htmlFor="enganche" className="text-[12px] font-semibold uppercase tracking-[0.14em] text-forest-900/70" data-id="element-84" data-mp="1pbkw95h51b8l">
                  Enganche
                </label>
                <span className="font-display text-2xl text-forest-900" data-id="element-85" data-mp="1gadatnzq04vx">
                  {formatQ(enganche)} · {enganchePct}%
                </span>
              </div>
              <input id="enganche" type="range" className="arco-slider mt-4" min={MIN_ENGANCHE} max={maxEnganche} step={500} value={enganche} onChange={e => setEngancheInput(Number(e.target.value))} data-id="element-86" data-mp="4vzr8vc02blp" />
              <div className="mt-2 flex justify-between text-[11px] uppercase tracking-[0.1em] text-forest-900/45" data-id="element-87" data-mp="6cwoq54osemu">
                <span data-id="element-88" data-mp="1c8znp00q7v9">Mínimo {formatQ(MIN_ENGANCHE)}</span>
                <span data-id="element-89" data-mp="39v6rrma59hd0">Máximo {formatQ(maxEnganche)}</span>
              </div>
            </div>

            <fieldset data-id="element-90" data-mp="35tcvrraw91j6">
              <legend className="text-[12px] font-semibold uppercase tracking-[0.14em] text-forest-900/70" data-id="element-91" data-mp="35u4psxouer7c">
                Plazo en meses
              </legend>
              <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-92" data-mp="3esq42gna7k2b">
                {PLAZOS.map(meses => <button key={meses} type="button" aria-pressed={plazo === meses} onClick={() => setPlazo(meses)} className={`min-w-[72px] rounded-full border px-5 py-2.5 text-[13px] font-semibold transition-colors duration-150 ease-smooth ${plazo === meses ? 'border-forest-900 bg-forest-900 text-white' : 'border-forest-900/20 text-forest-900/70 hover:border-forest-900/50'}`} data-id="element-93" data-mp="2r2vtk5qfq7l8">
                    {meses}
                  </button>)}
              </div>
            </fieldset>
          </div>

          {/* Resultado */}
          <div className="flex flex-col justify-center bg-forest-900 p-7 text-white sm:p-10" data-id="element-94" data-mp="3niyrjdemdlhw">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-400" data-id="element-95" data-mp="3sgqfxxg22phc">
              Su cuota mensual estimada
            </p>
            <p className="mt-3 font-display text-5xl leading-none text-white sm:text-6xl" data-id="element-96" data-mp="1y2pkznnmmmmj">
              {formatQ(cuota)}
            </p>
            <p className="mt-2 text-[13px] text-white/60" data-id="element-97" data-mp="231kaf8g3n9fm">
              durante {plazo} meses · tasa 18% anual sobre saldos
            </p>

            <dl className="mt-8 space-y-3 text-[13px]" data-id="element-98" data-mp="ffg02lj95fr1">
              <div className="flex justify-between border-b border-white/15 pb-3" data-id="element-99" data-mp="fdwi8qhy6wn5">
                <dt className="text-white/65" data-id="element-100" data-mp="bedzo1dlmxnh">Enganche a pagar</dt>
                <dd className="font-semibold" data-id="element-101" data-mp="6f1bndazrkjb">{formatQ(enganche)}</dd>
              </div>
              <div className="flex justify-between border-b border-white/15 pb-3" data-id="element-102" data-mp="134ifdmqeywid">
                <dt className="text-white/65" data-id="element-103" data-mp="132fnex5patb0">Monto a financiar</dt>
                <dd className="font-semibold" data-id="element-104" data-mp="z3opwoxbp9nd">{formatQ(financiar)}</dd>
              </div>
              <div className="flex justify-between" data-id="element-105" data-mp="u2sqoonx2boa">
                <dt className="text-white/65" data-id="element-106" data-mp="p599bfvx9is4">Total del plan</dt>
                <dd className="font-semibold" data-id="element-107" data-mp="p4r1rn3t7i4w">{formatQ(total)}</dd>
              </div>
            </dl>

            <button type="button" onClick={() => onSolicitar(`Quiero esta cuota: lote de ${formatQ(precio)}, enganche de ${formatQ(enganche)} (${enganchePct}%) y ${plazo} meses a ${formatQ(cuota)} mensuales.`)} className="mt-9 flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-[12px] font-semibold uppercase tracking-[0.14em] text-white transition-[background-color,transform] duration-150 ease-smooth hover:bg-gold-600 active:scale-[0.98]" data-id="element-108" data-mp="3es88iu55qq1s">
              Quiero esta cuota — contáctame
              <ArrowRightIcon size={16} data-id="element-109" data-mp="2xe72bzaw88j0" />
            </button>
            <p className="mt-4 text-[11px] leading-relaxed text-white/50" data-id="element-110" data-mp="2jlr5xhchz8r2">
              Cálculo estimado con fines informativos. La cuota final se define en el contrato de
              compraventa.
            </p>
          </div>
        </div>
      </div>
    </section>;
}